import React, { useState, useEffect } from 'react';

import {
  DefaultRoutes,
  EmployeeRoutes,
  FinanceRoutes,
  flattenRoutes,
  HRRoutes,
  ProjectAdminRoutes,
  SeniorManagerRoutes,
  TechCouncilRoutes,
} from './routes';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Provider } from 'react-redux';
import ProtectedRouteComponent from './components/ProtectedRouteComponent';
import ScrollToTop from './utils/ScrollToTop';
import Sidemenu from './components/BarsOverlay/BarsOverlayComponent';
import Breadcrumbs from './components/Breadcrumbs/Breadcrumbs';
import NotificationSelector from './NotificationSelector';
import { AdminRoutes } from './admin/components/AdminRoutes';
import KeycloakService from './services/KeycloakService';
import { configureStore } from '@reduxjs/toolkit';
import rootReducer from './modules';
import BlockingBanner from './components/BlockingBanner/BlockingBanner';
import WarningBanner from './components/WarningBanner/WarningBanner';
import { useRefreshMyStatus } from './hooks/queries/moodle/useRefreshMyStatus';
import { useGetMyBlockingDecision } from './hooks/queries/moodle/useGetMyBlockingDecision';
import { getHomeRouteForMode } from './utils/Routing';

const store = configureStore({ reducer: rootReducer });

/** Owns the pathname subscription for `/` → mode home redirect only. */
function RootRedirect({ mode }) {
  const location = useLocation();

  if (location.pathname !== '/') {
    return null;
  }

  return <Navigate to={getHomeRouteForMode(mode)} replace />;
}

function AppContent() {
  const [isAdmin, setIsAdmin] = React.useState(
    localStorage.getItem('mode') === 'Admin',
  );
  const [reportsSelected, setReportsSelected] = React.useState(
    localStorage.getItem('mode') === 'Report',
  );
  const [HRSelected, setHRSelected] = React.useState(
    localStorage.getItem('mode') === 'HR',
  );
  const [financeSelected, setFinanceSelected] = React.useState(
    localStorage.getItem('mode') === 'Finance',
  );
  const [seniorManagerSelected, setSeniorManagerSelected] = React.useState(
    localStorage.getItem('mode') === 'Senior-Manager',
  );
  const [techCouncilSelected, setTechCouncilSelected] = React.useState(
    localStorage.getItem('mode') === 'Tech-Council',
  );
  const [projectAdminSelected, setProjectAdminSelected] = React.useState(
    localStorage.getItem('mode') === 'Project-Admin',
  );
  const [validRoutes, setValidRoutes] = React.useState([]);
  const navigate = useNavigate();

  const currentMode = isAdmin
    ? 'Admin'
    : HRSelected
      ? 'HR'
      : financeSelected
        ? 'Finance'
        : seniorManagerSelected
          ? 'Senior-Manager'
          : techCouncilSelected
            ? 'Tech-Council'
            : projectAdminSelected
              ? 'Project-Admin'
              : 'User';

  const { blockingDecision: storedDecision } = useGetMyBlockingDecision({
    enabled: true,
  });

  const { mutate: checkBlockingStatus } = useRefreshMyStatus();

  useEffect(() => {
    checkBlockingStatus();
  }, [checkBlockingStatus]);

  const [roles, setRoles] = useState([
    { role: 'admin', route: AdminRoutes, url: '/admin-projects' },
    { role: 'hr', route: HRRoutes, url: '/hr-master-data' },
    { role: 'tech-council', route: TechCouncilRoutes, url: '/tech-council' },
    {
      role: 'finance',
      route: FinanceRoutes,
      url: '/finance-external',
    },
    {
      role: 'senior-manager',
      route: SeniorManagerRoutes,
      url: '/senior-manager-client-office',
    },
    { role: 'employee', route: EmployeeRoutes, url: '/user-timings' },
    {
      role: 'project-admin',
      route: ProjectAdminRoutes,
      url: '/project-admin',
    },
  ]);

  const getRoutes = () => {
    // Read path without useLocation so AppContent does not re-render on navigation.
    // Only consulted when mode/role deps change (same as prior effect timing).
    const path = window.location.pathname;
    switch (true) {
      case path.startsWith('/user-'):
        return KeycloakService.hasRole(['employee']) === true
          ? EmployeeRoutes
          : roles[0].route;
      case path.startsWith('/admin-'):
        return KeycloakService.hasRole(['admin']) === true
          ? AdminRoutes
          : roles[0].route;
      case path.startsWith('/hr-'):
        return KeycloakService.hasRole(['hr']) === true
          ? HRRoutes
          : roles[0].route;
      case path.startsWith('/finance-'):
        return KeycloakService.hasRole(['finance']) === true
          ? FinanceRoutes
          : roles[0].route;
      case path.startsWith('/senior-manager'):
        return KeycloakService.hasRole(['senior-manager']) === true
          ? SeniorManagerRoutes
          : roles[0].route;
      case path.startsWith('/tech-council'):
        return KeycloakService.hasRole(['tech-council']) === true
          ? TechCouncilRoutes
          : roles[0].route;
      case path.startsWith('/project-admin'):
        return KeycloakService.hasRole(['project-admin']) === true
          ? ProjectAdminRoutes
          : roles[0].route;
      default:
        if (isAdmin) return AdminRoutes;
        else if (HRSelected) return HRRoutes;
        else if (financeSelected) return FinanceRoutes;
        else if (seniorManagerSelected) return SeniorManagerRoutes;
        else if (techCouncilSelected) return TechCouncilRoutes;
        else if (projectAdminSelected) return ProjectAdminRoutes;
        else return EmployeeRoutes;
    }
  };

  const [ApplicationRoutes, setApplicationRoutes] = React.useState(
    flattenRoutes(
      AdminRoutes.concat(
        HRRoutes,
        FinanceRoutes,
        SeniorManagerRoutes,
        EmployeeRoutes,
        TechCouncilRoutes,
        ProjectAdminRoutes,
        DefaultRoutes,
      ),
    ),
  );

  useEffect(() => {
    const filteredRoles = getFilteredRoles();
    if (filteredRoles.length === 0) {
      navigate('/no-role');
    }
  }, []);

  const getFilteredRoles = () => {
    const filteredRoles = roles.filter((obj) => {
      if (obj.role === 'employee') {
        return (
          KeycloakService.hasRole([obj.role]) ||
          KeycloakService.hasRole(['manager'])
        );
      }
      return KeycloakService.hasRole([obj.role]);
    });
    setRoles((prev) => [...filteredRoles]);
    return filteredRoles;
  };
  useEffect(() => {
    const filteredRoutes = getRoutes().filter((routeComponent) =>
      KeycloakService.hasRole(['manager']) ||
      KeycloakService.hasRole(['senior-manager'])
        ? routeComponent.title !== 'Profile'
        : routeComponent.title !== 'Profile' &&
          routeComponent.title !== 'Manage',
    );
    setValidRoutes((prev) => {
      if (
        prev.length === filteredRoutes.length &&
        prev.every((route, index) => route.key === filteredRoutes[index].key)
      ) {
        return prev;
      }
      return filteredRoutes;
    });
  }, [
    isAdmin,
    reportsSelected,
    financeSelected,
    seniorManagerSelected,
    HRSelected,
    techCouncilSelected,
    roles,
    projectAdminSelected,
  ]);

  return (
    <>
      <RootRedirect mode={currentMode} />
      <Sidemenu
        roles={roles}
        sideMenuRoutes={validRoutes}
        isAdmin={isAdmin}
        reportsSelected={reportsSelected}
        setReportsSelected={setReportsSelected}
        HRSelected={HRSelected}
        setHRSelected={setHRSelected}
        techCouncilSelected={techCouncilSelected}
        setTechCouncilSelected={setTechCouncilSelected}
        financeSelected={financeSelected}
        setFinanceSelected={setFinanceSelected}
        seniorManagerSelected={seniorManagerSelected}
        setSeniorManagerSelected={setSeniorManagerSelected}
        setIsAdmin={setIsAdmin}
        projectAdminSelected={projectAdminSelected}
        setProjectAdminSelected={setProjectAdminSelected}
      />
      <div className="all-content-backdrop">
        <div className="main-content-container">
          <NotificationSelector>
            <ScrollToTop>
              <>
                <BlockingBanner data={storedDecision} />
                {!storedDecision?.blocked && <WarningBanner />}
              </>

              <Breadcrumbs />
              <Routes>
                {ApplicationRoutes.filter(
                  (route) => route.path && route.component,
                ).map((route) => (
                  <Route
                    key={route.key}
                    path={route.path + '/*'}
                    element={
                      <ProtectedRouteComponent roles={roles}>
                        <route.component />
                      </ProtectedRouteComponent>
                    }
                  />
                ))}
              </Routes>
            </ScrollToTop>
          </NotificationSelector>
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
